import React from "react";
import { Link as ReactRouterLink } from "react-router-dom";
import { useStoreTags, useStoreDebugTags } from "@hooks";
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Text,
  Flex,
  Heading,
  Link,
  ListItem,
  Tag,
  UnorderedList,
  CardProps,
} from "@chakra-ui/react";

import { ButtonModal } from "../ModalWrappers";

import {
  AddCommentModal,
  AddDetachCarrierModal,
  AddDetachVTRXModal,
  AddDetachSEHModal,
  AddDetachROHModal,
  AddImageModal,
  AddTagModal,
  AddDebugTagModal,
  RemoveDebugTagModal,
  AddVTRXTagModal,
  ShipModuleModal,
  AddDownloadDataModal,
  CardDivider,
} from "@components";
import {
  Module,
  StepFrontend,
  getModuleTypeFromId,
  getPartTypeFromSerialNumber,
  getHumanReadableStepName,
} from "@constants";
import { getNextStepFromModuleSteps } from "@utils";

type ModuleDetailsProps = CardProps & {
  dbModule: Module;
  mysteps: StepFrontend[];
};

export function ModuleDetails({
  dbModule,
  mysteps,
  ...delegated
}: ModuleDetailsProps) {
  const { id, location, status, serials, steps } = dbModule;
  const nextStep = getNextStepFromModuleSteps(getModuleTypeFromId(id), steps);
  const isAssembled = serials && serials.length >= 1;
  const badTags = useStoreTags();
  const debugTags = useStoreDebugTags();
  const hasVTRX = React.useMemo(
    () => serials?.some((s) => s.includes("VTR")),
    [serials],
  );
  const hasSEH = React.useMemo(
    () => serials?.some((s) => s.includes("SEH")),
    [serials],
  );
  const hasROH = React.useMemo(
    () => serials?.some((s) => s.includes("PSROH")),
    [serials],
  );
  let hasCarrier = false;
  let isDetached = false;
  for (const step in mysteps) {
    if (
      mysteps[step].stepName == "2S_GLUE_SANDWICH" ||
      mysteps[step].stepName == "PS_SENSOR_BASEPLATE_GLUING"
    ) {
      hasCarrier = true;
    }
    if (mysteps[step].stepName == "AUX_DETACH_CARRIER") {
      isDetached = true;
    }
  }
  if (isDetached) hasCarrier = false;
  let isIncomplete = false;
  if (dbModule && dbModule.tags) {
    for (const tag of dbModule.tags) {
      if (tag.toUpperCase() == "INCOMPLETE") {
        isIncomplete = true;
      }
    }
  }
  const hasDebugTag = dbModule.debugTags
    ? dbModule.debugTags.length > 0
      ? 1
      : 0
    : 0;
  let hasVTRXLength = false;
  let vtrxString = "";
  if (dbModule && dbModule.tags) {
    for (const tag of dbModule.tags) {
      if (tag.toUpperCase().search("VTRX") >= 0) {
        hasVTRXLength = true;
        vtrxString = tag;
      }
    }
  }

  const badModuleTags = new Array<string>();
  (dbModule.tags ?? []).forEach((tag) => {
    if (badTags.includes(tag)) {
      badModuleTags.push(tag);
    }
  });

  const debugModuleTags = new Array<string>();
  (dbModule.debugTags ?? []).forEach((tag) => {
    if (debugTags.includes(tag)) {
      debugModuleTags.push(tag);
    }
  });

  const is2S = nextStep.startsWith("2S") ? true : false;
  return (
    <Card marginTop="32px" {...delegated}>
      <CardHeader>
        <Heading as="h2">{id}</Heading>
      </CardHeader>
      <CardDivider />
      {location ? (
        <CardBody>
          <Heading as="h3" size="lg">
            Related Components
          </Heading>
          <UnorderedList alignItems="stretch" marginTop="4px">
            {Array.from(new Set(serials)).map((sn) => (
              <ListItem key={sn}>
                <Link
                  as={ReactRouterLink}
                  to={`/components/${sn}`}
                  display="flex"
                  flexDirection="row"
                  gap="4px"
                >
                  <Text>{sn}</Text>
                  <Text fontStyle="italic" size="sm" textColor="GrayText">
                    {getPartTypeFromSerialNumber(sn).shorthand}
                  </Text>
                </Link>
              </ListItem>
            ))}
          </UnorderedList>
          {nextStep !== "" ? (
            <Heading size="md" marginTop="8px">
              <Link
                as={ReactRouterLink}
                to={`/production/${nextStep}?moduleId=${dbModule.id}`}
              >
                Next Step: {getHumanReadableStepName(nextStep)}
              </Link>
            </Heading>
          ) : undefined}
        </CardBody>
      ) : (
        <CardBody>
          <Heading as="h3" size="lg">
            Module or component not found. If it was recently registered, please
            refresh the page.
          </Heading>
        </CardBody>
      )}
      <CardDivider />
      {location ? (
        <CardFooter>
          <Flex
            flexDirection="row"
            justifyItems="flex-start"
            alignContent="center"
            flexWrap="wrap"
            gap="8px"
          >
            <Tag size="md" borderRadius="full">
              {getModuleTypeFromId(id)} Module
            </Tag>
            <Tag size="md" borderRadius="full">
              {status}
            </Tag>
            <Tag size="md" borderRadius="full">
              @ {location.location}
            </Tag>
            {hasVTRXLength ? (
              <Tag size="md" borderRadius="full">
                {vtrxString}
              </Tag>
            ) : null}
            {badModuleTags.map((tag) => (
              <Tag size="md" borderRadius="full" colorScheme="red" key={tag}>
                {tag.toUpperCase().split("_").join(" ")}
              </Tag>
            ))}
            {debugModuleTags.map((tag) => (
              <Tag size="md" borderRadius="full" colorScheme="orange" key={tag}>
                {tag.toUpperCase().split("_").join(" ")}
              </Tag>
            ))}
            <AddImageModal itemId={dbModule.id} />
            <AddCommentModal itemId={dbModule.id} />
            <AddDebugTagModal itemId={dbModule.id} />
            <AddTagModal itemId={dbModule.id} />
            {hasDebugTag ? (
              <RemoveDebugTagModal
                itemId={dbModule.id}
                debugTags={debugModuleTags}
              />
            ) : null}
            {!hasVTRXLength ? <AddVTRXTagModal itemId={dbModule.id} /> : null}
            <ShipModuleModal itemId={dbModule.id} />
            {hasCarrier == true ? (
              <AddDetachCarrierModal itemId={dbModule.id} />
            ) : null}
            {hasVTRX == true ? (
              <AddDetachVTRXModal itemId={dbModule.id} />
            ) : null}
            {hasSEH == true ? <AddDetachSEHModal itemId={dbModule.id} /> : null}
            {hasROH == true ? <AddDetachROHModal itemId={dbModule.id} /> : null}
            {isAssembled == true ? (
              <AddDownloadDataModal
                dbModule={dbModule}
                steps={mysteps as StepFrontend[]}
              />
            ) : null}
            {isIncomplete == true && is2S ? (
              <ButtonModal label="Glue SEH" title="Glue Parts" size="sm">
                <Link
                  as={ReactRouterLink}
                  to={`/production/AUX_2S_EXTRA_GLUING`}
                >
                  Perform glue step for 2S
                </Link>
              </ButtonModal>
            ) : null}
            {isIncomplete == true && !is2S ? (
              <ButtonModal label="Glue ROH" title="Glue Parts" size="sm">
                <Link
                  as={ReactRouterLink}
                  to={`/production/AUX_PS_EXTRA_GLUING`}
                >
                  Perform glue step for PS
                </Link>
              </ButtonModal>
            ) : null}
          </Flex>
        </CardFooter>
      ) : null}
    </Card>
  );
}
